// src/components/stages/VitalsCoreFields.tsx
// The "Vitals" section's own physiological readings (BP, pulse, temp, SpO2,
// weight, height, auto BMI) — shared by VitalsStage.tsx's own Section A and
// ConsultStage.tsx's doctor-facing correction form (VitalsAmendForm), so a
// reading looks and warn-highlights identically wherever it's entered or
// corrected, using the same lib/vitalThresholds.ts checks both places
// already read from independently.
import { StatField } from '../ui/StatField';
import { checkBp, checkPulse, checkTemp, checkSpo2, checkBmi } from '../../lib/vitalThresholds';

export interface VitalsCoreValue {
  bp_sys: string;
  bp_dia: string;
  pulse: string;
  temp: string;
  spo2: string;
  weight: string;
  height: string;
}

export function computeBmi(weight: string, height: string): string {
  return weight && height ? (Number(weight) / ((Number(height) / 100) ** 2)).toFixed(1) : '';
}

export function VitalsCoreFields({ value, onChange }: {
  value: VitalsCoreValue;
  onChange: (key: keyof VitalsCoreValue, v: string) => void;
}) {
  const bmi = computeBmi(value.weight, value.height);
  const bpCheck = checkBp(value.bp_sys, value.bp_dia);
  const bpWarn = bpCheck.warn;
  const pulseWarn = checkPulse(value.pulse).warn;
  const tempWarn = checkTemp(value.temp).warn;
  const spo2Warn = checkSpo2(value.spo2).warn;
  const bmiWarn = checkBmi(bmi).warn;

  return (
    <div className="flex flex-wrap gap-2.5">
      {/* BP: a slightly wider tile, same height as the others. Each box carries
          its own SYS / DIA tag (always visible, unlike a placeholder) so the two
          numbers can't be confused. Same keys, same warning, same storage. */}
      <StatField icon="🫀" label="Blood pressure (mmHg)" warn={!!bpWarn} className="min-w-[220px] sm:basis-[240px] sm:grow-[1.6]">
        <div className="flex items-center gap-1.5">
          <label className="relative min-w-0 flex-1">
            <span aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[9.5px] font-bold uppercase tracking-wide text-ink-muted">Sys</span>
            <input
              type="number" inputMode="numeric" placeholder="120" aria-label="Systolic blood pressure, mmHg"
              className="w-full min-w-0 !pl-8 !pr-2 text-center text-[15px] font-semibold tabular-nums"
              value={value.bp_sys} onChange={e => onChange('bp_sys', e.target.value)}
            />
          </label>
          <span aria-hidden="true" className="text-[18px] font-light text-ink-muted">/</span>
          <label className="relative min-w-0 flex-1">
            <span aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[9.5px] font-bold uppercase tracking-wide text-ink-muted">Dia</span>
            <input
              type="number" inputMode="numeric" placeholder="80" aria-label="Diastolic blood pressure, mmHg"
              className="w-full min-w-0 !pl-8 !pr-2 text-center text-[15px] font-semibold tabular-nums"
              value={value.bp_dia} onChange={e => onChange('bp_dia', e.target.value)}
            />
          </label>
        </div>
        {bpWarn && bpCheck.hint && <div className="text-[11px] font-medium text-warn-ink">{bpCheck.hint} — check the reading</div>}
      </StatField>
      <StatField icon="💓" label="Pulse (bpm)" warn={!!pulseWarn}>
        <input type="number" className="w-full" value={value.pulse} onChange={e => onChange('pulse', e.target.value)} />
      </StatField>
      <StatField icon="🌡️" label="Temp (°F)" warn={!!tempWarn}>
        <input type="number" className="w-full" value={value.temp} onChange={e => onChange('temp', e.target.value)} />
      </StatField>
      <StatField icon="🫁" label="SpO₂ (%)" warn={!!spo2Warn}>
        <input type="number" className="w-full" value={value.spo2} onChange={e => onChange('spo2', e.target.value)} />
      </StatField>
      <StatField icon="⚖️" label="Weight (kg)">
        <input type="number" className="w-full" value={value.weight} onChange={e => onChange('weight', e.target.value)} />
      </StatField>
      <StatField icon="📏" label="Height (cm)">
        <input type="number" className="w-full" value={value.height} onChange={e => onChange('height', e.target.value)} />
      </StatField>
      <StatField icon="📊" label="BMI (auto)" warn={!!bmiWarn}>
        <div style={{ padding: '7px 9px', fontWeight: 700, fontSize: 14, color: bmiWarn ? 'var(--color-warn-ink)' : 'var(--color-ink)' }}>
          {bmi || '—'}
        </div>
      </StatField>
    </div>
  );
}
